/**
 * KNK-1.22 / FR-8 — invite to first knock, on the web.
 *
 * Two people use this. An admin invites a rep from Settings → Knock Mode. A rep opens the emailed
 * or texted link, app.joulescrm.com/join/<code>, sets a password, and is sent to the phone app.
 *
 * Rules and copy, with no React, so they can be tested. The data is migration
 * `20261005a6_knk_1_22_knock_invites.sql`, reached through joules-admin-backend
 * `/api/v1/knock-invites` (create, preview, accept) and directly for the list and revoke.
 * joules-mobile `lib/knockInvite.ts` is the phone's twin: keep the copy in step.
 *
 * ⚠️ NOT THE GATE. The database decides who may invite, that an invite only ever makes a Sales
 * Rep, and that a code lives 7 days. This file decides what to render.
 *
 * ⚠️ SMS IS DEFERRED (Allan, 2026-10-05). Joules does not text the link. The backend emails it
 * when there is an address, and the manager copies it into their own messages otherwise.
 */

export type InvitePreview =
  | {
      status: 'pending';
      accountName: string | null;
      invitedByName: string | null;
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
      email: string | null;
    }
  | { status: 'accepted' | 'expired' | 'revoked' | 'unknown' };

export interface CreatedInvite {
  inviteId: string;
  code: string;
  link: string;
  expiresAt: string;
  phone: string | null;
  email: string | null;
  smsBody: string;
  emailed: boolean;
}

export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface InviteRow {
  id: string;
  status: InviteStatus;
  label: string;
  detail: string;
  createdAt: string;
}

/** The JoulesCRM listings: App Store id is joules-mobile eas.json `ascAppId`, Play is its package. */
export const STORE_LINKS = {
  ios: 'https://apps.apple.com/app/id6749386247',
  android: 'https://play.google.com/store/apps/details?id=com.joulescrm',
} as const;

export const JOIN_COPY = {
  enterCodeTitle: 'Join your team',
  enterCodeHint: 'Enter the code from your invite. It is 10 letters and numbers, like ABCDE-12345.',
  codePlaceholder: 'ABCDE-12345',
  check: 'Continue',
  badCode: 'That is not an invite code. It is 10 letters and numbers, like ABCDE-12345.',
  checking: 'Checking your invite…',
  invited: (who: string | null, account: string | null) =>
    `${who ?? 'Your manager'} added you to ${account ?? 'their team'}.`,
  setPassword: 'Choose a password to finish. You will sign in to the Joules app with your email and this password.',
  firstNameLabel: 'First name',
  lastNameLabel: 'Last name',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  confirmLabel: 'Confirm password',
  join: 'Join',
  needEmail: 'Enter your email address. You will sign in with it.',
  badEmail: 'That email address does not look right.',
  shortPassword: 'Choose a password of at least 8 characters.',
  mismatch: 'The two passwords are different.',
  joinedTitle: 'You are in',
  joined: (email: string) => `Get the Joules app on your phone and sign in as ${email} with the password you just chose.`,
  appStore: 'Get it on the App Store',
  playStore: 'Get it on Google Play',
  byStatus: {
    accepted: 'This invite has already been used. Sign in to the Joules app instead.',
    expired: 'This invite has expired. Ask your manager for a new one.',
    revoked: 'This invite was withdrawn. Ask your manager for a new one.',
    unknown: 'That invite code is not valid. Check it, or ask your manager for a new one.',
  },
  unreachable: 'Could not reach Joules. Check your connection and try again.',
  // The manager's side
  inviteTitle: 'Invite reps',
  inviteHint:
    'A rep joins this account as a Sales Rep, sets their own password and starts knocking from the phone app. The link lasts 7 days.',
  phoneLabel: 'Mobile number',
  needContact: 'Enter a mobile number or an email address.',
  create: 'Create invite',
  createdTitle: 'Invite ready',
  createdHint: 'Copy the link and send it from your own phone or email. Joules texting it for you comes later.',
  emailed: (email: string) => `We also emailed it to ${email}.`,
  copyLink: 'Copy link',
  copied: 'Link copied',
  listTitle: 'Invites',
  listEmpty: 'No invites yet.',
  revoke: 'Revoke',
  revokeConfirm: (label: string) => `Revoke the invite for ${label}? Their link stops working.`,
  statusLabel: { pending: 'Pending', accepted: 'Joined', expired: 'Expired', revoked: 'Revoked' } as Record<InviteStatus, string>,
} as const;

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A code typed, pasted, or carried in a link, as XXXXX-XXXXX; null if it cannot be one. */
export function normalizeInviteCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const fromLink = /\/join\/([A-Za-z0-9-]+)/.exec(raw);
  const compact = (fromLink ? fromLink[1] : raw).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return /^[A-Z0-9]{10}$/.test(compact) ? `${compact.slice(0, 5)}-${compact.slice(5)}` : null;
}

export function parseInvitePreview(raw: unknown): InvitePreview {
  if (!isRecord(raw)) return { status: 'unknown' };
  if (raw.status === 'pending') {
    return {
      status: 'pending',
      accountName: str(raw.account_name),
      invitedByName: str(raw.invited_by_name),
      firstName: str(raw.first_name),
      lastName: str(raw.last_name),
      phone: str(raw.phone),
      email: str(raw.email),
    };
  }
  if (raw.status === 'accepted' || raw.status === 'expired' || raw.status === 'revoked') return { status: raw.status };
  return { status: 'unknown' };
}

export function parseCreatedInvite(raw: unknown): CreatedInvite | null {
  if (!isRecord(raw)) return null;
  const inviteId = str(raw.invite_id);
  const code = str(raw.code);
  const link = str(raw.link);
  const expiresAt = str(raw.expires_at);
  if (!inviteId || !code || !link || !expiresAt) return null;
  return {
    inviteId,
    code,
    link,
    expiresAt,
    phone: str(raw.phone),
    email: str(raw.email),
    smsBody: str(raw.sms_body) ?? link,
    emailed: raw.emailed === true,
  };
}

export function formatPhone(phone: string | null): string | null {
  if (!phone) return null;
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(phone);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : phone;
}

const STATUSES: readonly InviteStatus[] = ['pending', 'accepted', 'expired', 'revoked'];

/** One `knock_invite_list` row, ready to render; null if it does not parse. */
export function parseInviteRow(raw: unknown): InviteRow | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const status = STATUSES.find((s) => s === raw.status);
  const createdAt = str(raw.created_at);
  if (!id || !status || !createdAt || !str(raw.expires_at)) return null;
  const name = [str(raw.first_name), str(raw.last_name)].filter(Boolean).join(' ');
  const phone = formatPhone(str(raw.phone));
  const email = str(raw.email);
  return {
    id,
    status,
    label: name || phone || email || 'Unnamed invite',
    detail: [name ? phone : null, name || phone ? email : null, str(raw.team_name)].filter(Boolean).join(' · '),
    createdAt,
  };
}

/** What the join page checks before sending. The server repeats every check. */
export function acceptFormError(
  form: { email: string; password: string; confirm: string },
  invitedEmail: string | null,
): string | null {
  if (!invitedEmail) {
    const e = form.email.trim();
    if (!e) return JOIN_COPY.needEmail;
    if (!EMAIL_RE.test(e)) return JOIN_COPY.badEmail;
  }
  if (form.password.length < 8) return JOIN_COPY.shortPassword;
  if (form.password !== form.confirm) return JOIN_COPY.mismatch;
  return null;
}

/** What the invite form checks before sending. The database says whether the number can be texted. */
export function inviteFormError(form: { phone: string; email: string }): string | null {
  return form.phone.trim() || form.email.trim() ? null : JOIN_COPY.needContact;
}

/** The backend answers refusals as `{ detail: { message, code } }`, FastAPI's own as `{ detail: "..." }`. */
export function backendErrorMessage(body: unknown, fallback: string): string {
  if (isRecord(body)) {
    const d = body.detail;
    if (typeof d === 'string' && d) return d;
    if (isRecord(d) && typeof d.message === 'string' && d.message) return d.message;
  }
  return fallback;
}
